import { PlusOutlined } from "@ant-design/icons";
import { Col, Collapse, Row } from "antd";
import cuid from "cuid";
import { FieldArray } from "formik";
import { find, get } from "lodash";
import { memo } from "react";
import { BiCog, BiTrash } from "react-icons/bi";
import Button from "shared/components/Button";
import Card from "shared/components/Card";
import ConditionsEditor from "shared/components/ConditionsEditor";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import Spin from "shared/components/Spin";
import { useQuery } from "shared/hooks/useApi";
import { COLORS } from "shared/style/colors";
import graphql from "utils/api/graphql";
import { PROCESSES } from "utils/api/graphql/queries/processes";
import { bindArrayInputProps } from "utils/helpers/input";

const EMPTY_CONDITIONS = { operator: "AND", rules: [] };

const syncProcesses = (current, selectedProcessIds, setFieldValue) => {
  const next = selectedProcessIds.map((processId) => {
    const existing = find(current, { processId });
    return existing || { processId, documents: [] };
  });
  setFieldValue("generatedDocuments", next);
};

const DocumentRow = memo(
  ({
    processIndex,
    docIndex,
    docId,
    translation,
    templateOptions,
    values,
    formProps,
    onRemove,
    onUpdateConditions,
    getConditions,
  }) => {
    const conditions = getConditions(processIndex, docIndex);
    const rulesCount = get(conditions, "rules.length", 0);

    return (
      <div className="dynamic-documents__row m-bottom-16">
        <Row gutter={[16, 16]} align="middle">
          <Col span={10}>
            <Select
              label={translation.DYNAMIC.TEMPLATE}
              placeholder={translation.DYNAMIC.TEMPLATE}
              options={templateOptions}
              {...bindArrayInputProps({
                parent: `generatedDocuments.${processIndex}.documents`,
                index: docIndex,
                name: "templateId",
                values,
                ...formProps,
              })}
            />
          </Col>
          <Col span={10}>
            <Input
              label={translation.DYNAMIC.OUTPUT_NAME}
              placeholder={translation.DYNAMIC.OUTPUT_NAME}
              {...bindArrayInputProps({
                parent: `generatedDocuments.${processIndex}.documents`,
                index: docIndex,
                name: "outputName",
                values,
                ...formProps,
              })}
            />
          </Col>
          <Col span={4}>
            <div className="d-flex gap-8">
              <BiTrash
                color={COLORS.C_DANGER}
                size={24}
                className="dynamic-documents__action"
                onClick={onRemove}
              />
            </div>
          </Col>
        </Row>

        <Collapse
          className="m-top-8"
          items={[
            {
              key: `conditions-${docId}`,
              label: (
                <span>
                  <BiCog size={14} className="mr-6" />
                  {translation.DYNAMIC.CONDITIONS}
                  {rulesCount > 0 && ` (${rulesCount})`}
                </span>
              ),
              children: (
                <ConditionsEditor
                  conditions={conditions}
                  emptyMessage={translation.DYNAMIC.NO_CONDITIONS}
                  onOperatorChange={(operator) =>
                    onUpdateConditions(processIndex, docIndex, {
                      ...conditions,
                      operator,
                    })
                  }
                  onAddRule={() =>
                    onUpdateConditions(processIndex, docIndex, {
                      ...conditions,
                      rules: [
                        ...conditions.rules,
                        { key: "", op: "equals", value: "" },
                      ],
                    })
                  }
                  onRemoveRule={(ruleIndex) =>
                    onUpdateConditions(processIndex, docIndex, {
                      ...conditions,
                      rules: conditions.rules.filter((_, i) => i !== ruleIndex),
                    })
                  }
                  onUpdateRule={(ruleIndex, patch) =>
                    onUpdateConditions(processIndex, docIndex, {
                      ...conditions,
                      rules: conditions.rules.map((rule, i) =>
                        i === ruleIndex ? { ...rule, ...patch } : rule,
                      ),
                    })
                  }
                />
              ),
            },
          ]}
        />
      </div>
    );
  },
);

const DynamicDocuments = ({ translation, values, setFieldValue, formProps }) => {
  const { data: templatesData, loading: templatesLoading } = useQuery(
    graphql.queries.DOCUMENT_TEMPLATES,
    {
      variables: { take: 500, skip: 0 },
      fetchPolicy: "network-only",
    },
  );

  const { data: processesData, loading: processesLoading } = useQuery(
    PROCESSES,
    {
      variables: { take: 500, skip: 0 },
    },
  );

  const templateOptions = (templatesData?.documentTemplates?.data || []).map(
    ({ id, name }) => ({ value: id, label: name }),
  );

  const processes = processesData?.processes?.data || [];
  const generatedDocuments = get(values, "generatedDocuments", []);
  const selectedProcessIds = generatedDocuments.map(({ processId }) => processId);

  const getConditions = (processIndex, docIndex) =>
    get(
      values,
      `generatedDocuments.${processIndex}.documents.${docIndex}.conditions`,
      EMPTY_CONDITIONS,
    );

  const updateConditions = (processIndex, docIndex, conditions) => {
    setFieldValue(
      `generatedDocuments.${processIndex}.documents.${docIndex}.conditions`,
      conditions,
    );
  };

  if (templatesLoading || processesLoading) {
    return (
      <Card title={translation.DYNAMIC.TITLE} divider={false}>
        <Spin />
      </Card>
    );
  }

  return (
    <Card title={translation.DYNAMIC.TITLE} divider={false}>
      <Row gutter={[16, 16]} align="middle" className="m-bottom-16">
        <Col span={4}>{translation.DYNAMIC.PROCESSES}</Col>
        <Col span={20}>
          <Select
            mode="multiple"
            placeholder={translation.DYNAMIC.PROCESSES}
            options={processes.map(({ id, name }) => ({
              value: id,
              label: name,
            }))}
            value={selectedProcessIds}
            onChange={(processIds) =>
              syncProcesses(generatedDocuments, processIds, setFieldValue)
            }
          />
        </Col>
      </Row>

      {generatedDocuments.map(({ processId }, processIndex) => {
        const processName = get(
          find(processes, { id: processId }),
          "name",
          processId,
        );
        const documents = get(
          values,
          `generatedDocuments.${processIndex}.documents`,
          [],
        );

        return (
          <div key={processId} className="m-bottom-16">
            <h3 className="m-bottom-8">{processName}</h3>
            <FieldArray
              name={`generatedDocuments.${processIndex}.documents`}
              render={(arrayHelpers) => (
                <>
                  {documents.map((doc, docIndex) => (
                    <DocumentRow
                      key={doc.id}
                      docId={doc.id}
                      processIndex={processIndex}
                      docIndex={docIndex}
                      translation={translation}
                      templateOptions={templateOptions}
                      values={values}
                      formProps={formProps}
                      onRemove={() => arrayHelpers.remove(docIndex)}
                      onUpdateConditions={updateConditions}
                      getConditions={getConditions}
                    />
                  ))}

                  <Button
                    type="primary"
                    onClick={() =>
                      arrayHelpers.push({
                        id: cuid(),
                        templateId: "",
                        outputName: "",
                        conditions: EMPTY_CONDITIONS,
                      })
                    }
                  >
                    <PlusOutlined color={COLORS.C_WHITE} />
                    {translation.DYNAMIC.ADD_DOCUMENT}
                  </Button>
                </>
              )}
            />
          </div>
        );
      })}
    </Card>
  );
};

export default DynamicDocuments;
